import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useCampaignContestDetailFetch } from '@pages/Traders-Room/Campaign-Results/Contest/hooks/useCampaignContestDetailsFetch'
import { useCampaignContestFile } from '@pages/Traders-Room/Campaign-Results/Contest/hooks/useCampaignContestFile'
import { useCampaignContestResultsFetch } from '@pages/Traders-Room/Campaign-Results/Contest/hooks/useCampaignContestResultsFetch'
import { useCampaignContestSearch } from '@pages/Traders-Room/Campaign-Results/Contest/hooks/useCampaignContestSearch'
import { useCampaignContestSort } from '@pages/Traders-Room/Campaign-Results/Contest/hooks/useCampaignContestSort'
import { useCampaignContestValues } from '@pages/Traders-Room/Campaign-Results/Contest/hooks/useCampaignContestValues'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { PageQuery } from '@utils/ApiClient'
import { useWindowResize } from '@utils/domUtils'

import { ClientContestCampaignDetailDto } from '@model/ClientContestCampaignDetailDto'
import { ClientContestCampaignResultDto } from '@model/ClientContestCampaignResultDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { Notice } from '@shared/components/Notice'

import { CampaignContestCards } from './CampaignContestCards'
import { CampaignContestTable } from './CampaignContestTable'

const isDisqualifiedRanking = (ranking?: number) => ranking === -1

export const CampaignContestPage: React.FC = () => {
  const { accountId, campaignId } = useParams<{ accountId: string; campaignId: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { scrollIntoView } = useScrollToElementIds()
  const { formatMoney } = useFormatNumber()

  const detail = useCampaignContestDetailFetch(accountId, campaignId)

  const sort = useCampaignContestSort(detail.campaignData.data)
  const search = useCampaignContestSearch(sort)

  const results = useCampaignContestResultsFetch(accountId, campaignId, search)

  const isLoading = [detail.isLoading, results.isLoading].some(Boolean)
  const isInitialLoading = detail.isInitialLoading
  const { meta, pageQuery, setPageQuery } = results

  const campaignValues = useCampaignContestValues(detail.campaignData.data)
  const generatedDocumentFile = useCampaignContestFile({
    data: results.data,
    campaignName: detail.campaignData.data?.name,
  })

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)

  useEffect(() => {
    if (isPaginationEntrySelected && !detail.campaignData.isLoading && !results.isLoading) {
      scrollIntoView([ScrollToIds.CampaignResultsHeader])
      setIsPaginationEntrySelected(false)
    }
  }, [detail.campaignData.isLoading, results.isLoading, isPaginationEntrySelected, scrollIntoView])

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    if (pageSize && pageSize !== meta?.pageSize) setIsPaginationEntrySelected(true)
    setPageQuery!({ ...pageQuery, pageIndex, pageSize })
  }

  const handleSortChange = useCallback(
    (next?: PageQuery) => {
      if (!next) return
      search.setPageQuery(next)
    },
    [search.setPageQuery]
  )

  return (
    <>
      <Loading showLoadingIcon isLoading={isInitialLoading}>
        <PageSection
          id={ScrollToIds.CampaignResultsHeader}
          title={`${t('Tabs.Campaign Results')} / ${detail.campaignData.data?.name ?? ''}`}
          onGoBack={() => navigate(-1)}
          sortOptions={sort.sortOptions}
          pageQuery={search.pageQuery}
          setPageQuery={handleSortChange}
          documentFile={results.hasInitialResults ? generatedDocumentFile : undefined}
        />

        <TableSummary
          isLoading={isLoading}
          summaryItems={[
            {
              title: t('Campaigns.My Ranking'),
              value: !isDisqualifiedRanking(detail.campaignData.data?.ranking)
                ? detail.campaignData.data?.ranking?.toString() || t('N/A')
                : t('N/A'),
              infoTooltip: t(
                'Campaigns.Your current position in the Trading Challenge ranking. Updates hourly'
              ),
            },
            {
              title: t('Campaigns.Max Drawdown'),
              value: `${formatMoney(
                detail.drawdown.data?.maxDrawdown,
                detail.campaignData.data?.tradingAccount?.currencyId ?? 'USD'
              )} (${detail.drawdown.data?.maxDrawdownPercentage}%)`,
              infoTooltip: t('Campaigns.Maximum drawdown tooltip message', {
                maxDrawdown: detail.drawdown.data?.maxDrawdownPercentage,
              }),
              condition:
                !!detail.drawdown.data?.maxDrawdownPercentage &&
                !!detail.campaignData.data?.tradingAccount?.currencyId,
            },
            {
              title: campaignValues.getCampaignContestPointsCurrentValue(),
              value:
                campaignValues.getCampaignContestPointsSymbolCurrent() === '$'
                  ? `$${detail.campaignData.data?.points}`
                  : `${
                      detail.campaignData.data?.points
                    }${campaignValues.getCampaignContestPointsSymbolCurrent()}`,
              infoTooltip: campaignValues.getCampaignContestPointsDescription(),
              condition: !!detail.campaignData.data?.calculationType,
            },
            {
              title: t('Campaigns.Current Drawdown'),
              value: `${formatMoney(
                detail.drawdown.data?.currentDrawdown,
                detail.campaignData.data?.tradingAccount?.currencyId ?? 'USD'
              )} (${detail.drawdown.data?.currentDrawdownPercentage}%)`,
              infoTooltip: t(
                'Campaigns.Your current drawdown, if you cross the max drawdown, you will be disqualified'
              ),
              condition:
                !!detail.drawdown.data?.currentDrawdownPercentage &&
                !!detail.campaignData.data?.tradingAccount?.currencyId,
            },
            {
              title: campaignValues.getTradingDaysValue(detail?.days.data?.businessRule),
              value: detail?.days.data?.currentTradingDays ?? 0,
              infoTooltip: t('Campaigns.Number of days in which you opened at least one trade', {
                days: detail?.days.data?.minTradingDays ?? 0,
              }),
              condition:
                !!detail?.days.data?.currentTradingDays && !!detail?.days.data?.minTradingDays,
            },
          ]}
        />

        {detail.campaignData?.data?.disqualified && (
          <Box pt='x16'>
            <Notice
              id='disqualified-campaign'
              message={t("Campaigns.You've been disqualified from the competition")}
              type='warning'
            />
          </Box>
        )}

        <Loading isLoading={isLoading} showLoadingIcon>
          {detail.campaignData.data && (
            <CampaignContestRecords
              data={results.data}
              campaignDetails={detail.campaignData.data}
              sort={sort}
              search={search}
              campaignValues={campaignValues}
            />
          )}
        </Loading>

        {meta && (
          <Pagination
            scrollToHeaderId={ScrollToIds.CampaignResultsHeader}
            pageData={meta}
            onPageChanged={handlePageChange}
          />
        )}
      </Loading>
    </>
  )
}

interface CampaignContestRecordsProps {
  data: ClientContestCampaignResultDto[]
  campaignDetails?: ClientContestCampaignDetailDto
  sort: ReturnType<typeof useCampaignContestSort>
  search: ReturnType<typeof useCampaignContestSearch>
  campaignValues: ReturnType<typeof useCampaignContestValues>
}

const CampaignContestRecords: React.FC<CampaignContestRecordsProps> = (props) => {
  const { data, campaignDetails, sort, search, campaignValues } = props
  const isMobile = useWindowResize()

  if (isMobile && campaignDetails) {
    return (
      <CampaignContestCards
        data={data}
        campaignDetails={campaignDetails}
        campaignContestPointsValue={campaignValues.getCampaignContestPointsValue()}
        campaignContestPointsSymbol={campaignValues.getCampaignContestPointsSymbol()}
      />
    )
  }

  if (campaignDetails) {
    return (
      <CampaignContestTable
        data={data}
        campaignDetails={campaignDetails}
        sortOptions={sort.sortOptions}
        pageQuery={search.pageQuery}
        setPageQuery={search.setPageQuery}
        campaignContestPointsSymbol={campaignValues.getCampaignContestPointsSymbol()}
      />
    )
  }

  return null
}
